// src\modules\Orders\components\interaction\DropdownActions.tsx

import {
  faArrowsRotate,
  faCircleCheck,
  faCircleXmark,
  faCopy,
  faEllipsis,
  faEye,
  faPaperclip,
  faPrint,
  faSquareMinus,
  faTag,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import {
  RefreshLogisticsResultModal,
  RefreshLogisticsResultModalRef,
  UpdateFromStoreModal,
  UpdateFromStoreModalRef,
} from '../modals';

import { createHttpClient } from '@shared/adapters/HttpClient';
import { OrderDuplicationAdapter } from '@shared/adapters/OrderDuplicationAdapter';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { apiClientWH } from '@shared/services';
import { DuplicateOrderService } from '@shared/services/DuplicateOrderService';
import { OrdersApiAdapter } from '../../adapters/OrdersApiAdapter';
import {
  OrderStatus,
  getDropdownActionsByStatus,
  injectAttachmentsDropdownAction,
  injectRefreshLogisticsDropdownAction,
} from '../../config/orderStatusConfig';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { AttachmentsModal } from '@modules/Orders/ViewOrders/components/modals/AttachmentsModal';
import type { OrderAttachment } from '../../models/Orders';

interface DropdownActionsProps {
  status: OrderStatus;
  onAction?: (action: string) => void;
  orderNumber?: string; // Número del pedido para mostrar en las confirmaciones
  orderId?: number; // ID real del pedido para acciones API
  channelId?: number; // ID del canal de ventas (1=Shopify, 3=WooCommerce)
  attachments?: OrderAttachment[];
}

export const DropdownActions = ({
  status,
  onAction,
  orderNumber,
  orderId,
  channelId,
  attachments,
}: DropdownActionsProps) => {
  const { t } = useTranslation('ordersModule');
  const navigate = useNavigate();
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const updateFromStoreModalRef = useRef<UpdateFromStoreModalRef>(null);
  const refreshLogisticsResultModalRef = useRef<RefreshLogisticsResultModalRef>(null);
  const attachmentsModalRef = useRef<ModalCenterRef>(null);
  const [fetchedAttachments, setFetchedAttachments] = useState<OrderAttachment[]>([]);
  const { toast } = useToast();
  const { hasPermission, canAccess } = usePermissions();
  const api = new OrdersApiAdapter(createHttpClient(apiClientWH));

  // 🎯 Verificar permisos de pedidos
  const canConfirm = hasPermission('orders.confirm');
  const canCancel = hasPermission('orders.cancel');
  const canUncancel = hasPermission('orders.uncancel');

  // Función para obtener las acciones según el estado
  const getActionsByStatus = (status: OrderStatus) => {
    return getDropdownActionsByStatus(
      status,
      t,
      {
        faCircleXmark,
        faArrowsRotate,
        faCircleCheck,
        faCopy,
        faSquareMinus,
        faEye,
        faTag,
        faPrint,
        faXmark,
      },
      true, // Mostrar todas las acciones sin restricciones de permisos
    );
  };

  const allActions = getActionsByStatus(status);

  // 🎯 Filtrar acciones según permisos
  const actionsFiltered = allActions.filter((action) => {
    // Ver pedido siempre está disponible si se tiene orders.view (ya verificado en la ruta)
    if (action.action === 'view_order') return true;

    // Cancelar requiere orders.cancel
    if (action.action === 'cancel') return canCancel;

    // Marcar para envío requiere orders.confirm
    if (action.action === 'mark_send') return canConfirm;

    // Descancelar requiere orders.uncancel
    if (action.action === 'uncancel') return canUncancel;

    // Otras acciones (duplicate, update_store, labels, unassign_stock) no requieren permisos específicos
    return true;
  });

  const actions = injectAttachmentsDropdownAction(
    injectRefreshLogisticsDropdownAction(actionsFiltered, {
      enabled: canAccess(['tenant_3pl']),
      status,
      t,
      icon: faArrowsRotate,
      insertMode: 'after_view_order',
    }),
    {
      enabled: (attachments?.length ?? 0) > 0 || attachments === undefined,
      t,
      icon: faPaperclip,
      insertMode: 'after_view_order',
    },
  );

  // Función para identificar acciones sin implementar
  const isActionDisabled = (actionId: string): boolean => {
    const disabledActions: string[] = [
      // 'update_store', // Ya no está deshabilitada
      // 'duplicate', // Ya no está deshabilitada
    ];
    return disabledActions.includes(actionId);
  };

  const handleDuplicateOrder = async () => {
    if (!orderId) {
      toast({
        title: t('actions.duplicate.error.title'),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Obtener los datos completos de la orden
      const orderData = await api.getOrderById(orderId);

      // Convertir al formato para duplicación
      const duplicateData = await OrderDuplicationAdapter.adaptOrderForDuplication(orderData);

      // Generar una referencia única
      duplicateData.orderInfo.reference = OrderDuplicationAdapter.generateDuplicateReference(orderData.ref);

      // Guardar en localStorage
      DuplicateOrderService.saveDuplicateData(duplicateData);

      // Mostrar toast de éxito
      toast({
        title: 'Datos preparados',
        description: 'Los datos han sido cargados para crear una nueva orden',
        variant: 'default',
      });

      // Navegar a crear orden
      navigate('/orders/create');
    } catch (error) {
      console.error('Error duplicating order:', error);
      toast({
        title: t('actions.duplicate.error.title'),
        description: t('actions.duplicate.error.description'),
        variant: 'destructive',
      });
    }
  };

  // 🎯 Función para manejar la impresión de etiquetas
  const handlePrintLabel = async () => {
    if (!orderId) {
      toast({
        title: t('toast.print_error.title', 'Error'),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }

    // Siempre usar PDF como método de impresión
    const printingMethod = 'pdf';

    try {
      // Llamar a la API para imprimir
      const response = await api.printOrder(orderId, printingMethod);

      // Verificar que la respuesta tenga el link
      // La respuesta puede venir como string directo, array o como objeto
      let linkUrl = null;

      if (typeof response === 'string') {
        // Si la respuesta ES directamente el string del link
        linkUrl = response;
      } else if (Array.isArray(response) && response.length > 0) {
        // Si es un array, tomar el primer elemento
        linkUrl = response[0];
      } else if (response && response.link) {
        // Si es un objeto con propiedad link
        linkUrl = response.link;
      }

      if (linkUrl) {
        // Mostrar toast de éxito ANTES de abrir la pestaña
        toast({
          title: t('toast.print_success.title', 'Etiqueta generada'),
          description: t(
            'toast.print_success.description',
            'La etiqueta fue generada y será redirigida en unos segundos',
          ),
          variant: 'default',
        });

        // Pequeño delay para asegurar que el toast se muestre
        setTimeout(() => {
          // Asegurar que linkUrl sea un string válido
          const finalUrl = String(linkUrl).trim();

          if (finalUrl && finalUrl.startsWith('http')) {
            window.open(finalUrl, '_blank');
          } else {
            toast({
              title: t('toast.print_error.title', 'Error'),
              description: 'URL de la etiqueta no es válida',
              variant: 'destructive',
            });
          }
        }, 100);
      } else {
        toast({
          title: t('toast.print_error.title', 'Error'),
          description: 'No se recibió el link de la etiqueta',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      // Mostrar error de la API
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('toast.print_error.description', 'Error al generar la etiqueta');

      toast({
        title: t('toast.print_error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleRefreshLogistics = () => {
    if (!orderId) {
      toast({
        title: t('toast.error.title'),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }
    refreshLogisticsResultModalRef.current?.open({
      orderId,
      onClose: () => onAction?.('refresh_logistics'),
    });
  };

  const handleViewAttachments = async () => {
    if (!orderId) {
      toast({
        title: t('toast.error.title'),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }

    try {
      let items = attachments;
      if (!items?.length) {
        const orderData = await api.getOrderById(orderId);
        items = orderData.attachments;
      }

      if (!items?.length) {
        toast({
          title: t('toast.error.title'),
          description: t('actions.view_attachments.empty'),
          variant: 'destructive',
        });
        return;
      }

      setFetchedAttachments(items);
      setTimeout(() => attachmentsModalRef.current?.openModal(), 0);
    } catch (error: any) {
      const apiMessage = error.response?.data?.error?.message || error.message || t('toast.error.description');

      toast({
        title: t('toast.error.title'),
        description: apiMessage,
        variant: 'destructive',
      });
    }
  };

  const handleAction = async (action: any) => {
    // 🎯 "Ver pedido" ahora se maneja directamente con un enlace en el renderizado
    // para permitir Ctrl/Cmd + click y click en la rueda

    // Si la acción está deshabilitada, no hacer nada
    if (isActionDisabled(action.action)) {
      return;
    }

    // Manejar duplicación de orden
    if (action.action === 'duplicate') {
      await handleDuplicateOrder();
      return;
    }

    if (action.action === 'refresh_logistics') {
      handleRefreshLogistics();
      return;
    }

    if (action.action === 'view_attachments') {
      await handleViewAttachments();
      return;
    }

    // Caso especial para "Actualizar desde tienda" - usar modal personalizado
    if (action.action === 'update_store') {
      if (!orderId) {
        toast({
          title: t('toast.error.title'),
          description: 'ID de orden no disponible',
          variant: 'destructive',
        });
        return;
      }
      updateFromStoreModalRef.current?.open(orderId, channelId, () => {
        // Refrescar la lista tras la acción exitosa
        onAction?.(action.action);
      });
      return;
    }

    // Caso especial para "Ver etiqueta" - ejecutar directamente sin confirmación
    if (action.action === 'labels') {
      await handlePrintLabel();
      return;
    }

    // TODO: Si en el futuro se quiere restaurar la confirmación, descomentar el código siguiente:
    // if (action.action === 'labels') {
    //   confirmModalRef.current?.open(
    //     async () => {
    //       await handlePrintLabel();
    //     },
    //     undefined, // onCancel
    //     'Ver etiqueta',
    //     `¿Está seguro de que desea ver la etiqueta del pedido ${orderNumber || 'N/A'}?`,
    //     t('buttons.confirm'),
    //     t('buttons.cancel')
    //   );
    //   return;
    // }

    if (action.requiresConfirmation) {
      // Mostrar modal de confirmación
      const confirmationKey = action.confirmationKey;
      const title = t(`confirmModals.${confirmationKey}.title`);
      const message = t(`confirmModals.${confirmationKey}.message`, {
        orderNumber: orderNumber || 'N/A',
      });

      confirmModalRef.current?.open(
        async () => {
          if (!orderId) {
            console.error('orderId es requerido para esta acción');
            return;
          }

          try {
            switch (action.action) {
              case 'cancel':
                await api.cancelOrder(orderId);
                toast({
                  title: t('toast.cancel_success.title'),
                  description: t('toast.cancel_success.description', {
                    orderNumber,
                  }),
                  variant: 'success',
                });
                break;
              case 'uncancel':
                await api.uncancelOrder(orderId);
                toast({
                  title: t('toast.uncancel_success.title'),
                  description: t('toast.uncancel_success.description', {
                    orderNumber,
                  }),
                  variant: 'success',
                });
                break;
              case 'mark_send':
                await api.confirmOrder(orderId);
                toast({
                  title: t('toast.mark_send_success.title'),
                  description: t('toast.mark_send_success.description', {
                    orderNumber,
                  }),
                  variant: 'success',
                });
                break;
              case 'update_store':
                // TODO: Implementar actualización desde tienda
                toast({
                  title: t('toast.update_store_success.title', 'Actualizado'),
                  description: t('toast.update_store_success.description', {
                    orderNumber,
                  }),
                  variant: 'success',
                });
                break;
              case 'unassign_stock':
                try {
                  await api.unassignStock(orderId);
                  toast({
                    title: t('toast.unassign_stock_success.title', 'Stock desasignado'),
                    description: t('toast.unassign_stock_success.description', {
                      orderNumber,
                    }),
                    variant: 'success',
                  });
                } catch (error) {
                  const apiMessage =
                    (error as any)?.response?.data?.error?.message ||
                    (error as any)?.message ||
                    t('toast.error.description');
                  toast({
                    title: t('toast.error.title'),
                    description: apiMessage,
                    variant: 'destructive',
                  });
                }
                break;
              default:
                return;
            }

            // Refrescar la lista tras la acción exitosa
            onAction?.(action.action);
          } catch (error) {
            console.error('Error en acción:', error);
            // Obtener mensaje de la API si está disponible
            const apiMessage =
              (error as any)?.response?.data?.error?.message || (error as any)?.message || t('toast.error.description');

            toast({
              title: t('toast.error.title'),
              description: apiMessage,
              variant: 'destructive',
            });
          }
        },
        undefined, // onCancel opcional
        title,
        message,
        t('buttons.confirm', 'Confirmar'),
        t('buttons.cancel', 'Cancelar'),
      );
    } else {
      // Acción directa sin confirmación
      if (onAction) {
        onAction(action.action);
      }
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="hover:bg-muted rounded-md p-2" data-testid="dropdown-trigger">
            <FontAwesomeIcon icon={faEllipsis} className="text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52 border-card-border" data-testid="dropdown-content">
          {actions.map((action) => {
            // 🎯 Para "Ver pedido", usar un enlace para permitir Ctrl/Cmd + click y click en la rueda
            if (action.action === 'view_order' && orderId) {
              return (
                <DropdownMenuLinkItem key={action.id} to={`/orders/view/${orderId}`}>
                  <FontAwesomeIcon icon={action.icon} className="mr-2 w-3.5" />
                  {action.label}
                </DropdownMenuLinkItem>
              );
            }

            // 🎯 Para otras acciones, mantener el comportamiento original
            return (
              <DropdownMenuItem
                key={action.id}
                onClick={() => handleAction(action)}
                className={`whitespace-nowrap ${
                  isActionDisabled(action.action) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isActionDisabled(action.action)}
              >
                <FontAwesomeIcon icon={action.icon} className="mr-2 w-3.5" />
                {action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modal de confirmación */}
      <ConfirmModal ref={confirmModalRef} />

      {/* Modal personalizado para "Actualizar desde tienda" */}
      <UpdateFromStoreModal ref={updateFromStoreModalRef} />

      <RefreshLogisticsResultModal ref={refreshLogisticsResultModalRef} />

      <AttachmentsModal ref={attachmentsModalRef} attachments={fetchedAttachments} />
    </>
  );
};
